import AsyncStorage from '@react-native-async-storage/async-storage';

// Which user the local PowerSync database belongs to. Persisted (rather than held
// in memory) so the answer survives a force-quit: a sign-out whose clear failed,
// or a process killed mid-sign-out, both leave the previous owner recorded, and
// the next sign-in clears on the mismatch instead of layering a new user's session
// on top of the old user's rows.
//
// AsyncStorage is already the Supabase session store (lib/supabase.ts), so this
// key lives beside the session it describes.
export const CACHE_OWNER_KEY = 'rbf.powersync.cacheOwner';

/**
 * The user id the local database currently holds data for, or null if no account has
 * claimed it.
 *
 * Null is NOT a promise that the database is empty. The owner is written before
 * `connect()` and before any screen can write, so on this device's own history a null
 * owner does imply an empty database — but the two stores can skew independently (this
 * key lives in AsyncStorage, the rows live in SQLite), and a wiped AsyncStorage would
 * then present the previous account's rows as unowned. Callers must treat null as a
 * mismatch, not as an invitation to keep the rows.
 *
 * Storage errors propagate. There is no safe default: guessing "same owner" hands
 * the previous user's rows to the next one, and guessing "different owner" wipes
 * the legitimate owner's un-uploaded writes. Callers fail closed instead.
 */
export async function readCacheOwner(): Promise<string | null> {
  return AsyncStorage.getItem(CACHE_OWNER_KEY);
}

export async function writeCacheOwner(userId: string): Promise<void> {
  await AsyncStorage.setItem(CACHE_OWNER_KEY, userId);
}

export async function clearCacheOwner(): Promise<void> {
  await AsyncStorage.removeItem(CACHE_OWNER_KEY);
}
